import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_DOC_AUDIENCE,
  type DocAccess,
  type DocAudience,
  type DocAccessUpdate,
} from '@awwyeah/shared';
import { fetchDocAudience, fetchMyDocAccess, saveDocAudience } from './api';

/** The audience value the picker edits — the pair, because `members` needs both halves. */
export interface AudienceValue {
  audience: DocAudience;
  memberIds: string[];
}

export interface DocAudienceState extends AudienceValue {
  /** `null` until loaded, or for a doc nobody has claimed. */
  ownerId: string | null;
  canManage: boolean;
  loading: boolean;
  /** Last failure, as the server's error code (`not_the_owner`, `network_error`, …). */
  error: string | null;
  save: (next: AudienceValue) => Promise<boolean>;
  reload: () => void;
}

/**
 * One doc's audience, read and written through `/api/sharing`.
 *
 * Optimistic on save (the picker should feel like the rest of the app, which is instant
 * because it is CRDT-backed) but it rolls back on failure — unlike a Yjs write, this one
 * really can be refused, and quietly showing "Just me" for a list that is still shared
 * would be the worst possible lie for this particular screen to tell.
 *
 * Pass `docName: null` to hold the hook in a disabled state (a sheet that is closed).
 */
export function useDocAudience(docName: string | null): DocAudienceState {
  const [state, setState] = useState<
    AudienceValue & { ownerId: string | null; canManage: boolean }
  >({ audience: DEFAULT_DOC_AUDIENCE, memberIds: [], ownerId: null, canManage: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!docName) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchDocAudience(docName)
      .then((res) => {
        if (cancelled) return;
        setState({
          audience: res.audience,
          memberIds: [...res.memberIds],
          ownerId: res.ownerId,
          canManage: res.canManage,
        });
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'request_failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [docName, nonce]);

  const save = useCallback(
    async (next: AudienceValue): Promise<boolean> => {
      if (!docName) return false;
      const previous = state;
      setState((current) => ({ ...current, ...next }));
      setError(null);
      try {
        const update: DocAccessUpdate = { audience: next.audience, memberIds: next.memberIds };
        const res = await saveDocAudience(docName, update);
        setState({
          audience: res.audience,
          memberIds: [...res.memberIds],
          ownerId: res.ownerId,
          canManage: res.canManage,
        });
        return true;
      } catch (e: unknown) {
        setState(previous); // roll back — a refused permission change must not look applied
        setError(e instanceof Error ? e.message : 'request_failed');
        return false;
      }
    },
    [docName, state],
  );

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { ...state, loading, error, save, reload };
}

/**
 * Every ACL row that concerns me, as `docName -> DocAccess`. The index screens use it to
 * badge rows (🔒 / 👥) without a request per list.
 */
export function useMyDocAccess(): {
  byDocName: Map<string, DocAccess>;
  loading: boolean;
  reload: () => void;
} {
  const [entries, setEntries] = useState<DocAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchMyDocAccess()
      .then((rows) => {
        if (!cancelled) setEntries(rows);
      })
      .catch(() => {
        // Offline-first: no rows is the same answer as "everything is family", which is
        // the safe direction to fail in a *read* (the server still refuses the socket).
        if (!cancelled) setEntries([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  const byDocName = useMemo(
    () => new Map(entries.map((entry) => [entry.docName, entry])),
    [entries],
  );

  return { byDocName, loading, reload: useCallback(() => setNonce((n) => n + 1), []) };
}
